/**
 * FlowResultsPanel — results panel for flow visualization.
 *
 * Shows flow tree visualization with KPI bar and breadcrumb.
 * Handles loading, error, and empty states.
 */
import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { GitBranch, Play, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { fadeInUp } from '@/design/animations';
import { FlowVisualization } from './FlowVisualization';
import { FlowGraphBuilder } from './FlowGraphEngine';
import type { FlowRecord, FlowLevel } from './flow.types';

/* ─── Props ───────────────────────────────────────────────────────────────── */

interface FlowResultsPanelProps {
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether the query errored */
  isError: boolean;
  /** Error object */
  error: Error | null;
  /** Flow records from API */
  records: FlowRecord[] | null;
  /** Whether a query has been run */
  hasRun: boolean;
  /** Whether the query can be run */
  canRun: boolean;
  /** Run callback */
  onRun: () => void;
  /** Current tree levels */
  levels: FlowLevel[];
  /** Levels change callback */
  onLevelsChange: (levels: FlowLevel[]) => void;
  /** KPI event */
  kpiEvent?: string;
  /** Accent color */
  accentColor: string;
  /** Callback when a (none) node is clicked for continuation */
  onContinuationRequest?: (level: number, sessionIndex: number) => void;
  /** Set of level indices where continuation is loading */
  continuationLoadingLevels?: Set<number>;
  /** Continuation records by session index — passed through to FlowVisualization */
  continuationRecordsMap?: Map<number, FlowRecord[]>;
}

export function FlowResultsPanel({
  isLoading,
  isError,
  error,
  records,
  hasRun,
  canRun,
  onRun,
  levels,
  onLevelsChange,
  kpiEvent,
  accentColor,
  onContinuationRequest,
  continuationLoadingLevels,
  continuationRecordsMap,
}: FlowResultsPanelProps) {
  /* ── Derived stats ─────────────────────────────────────────────────────── */
  const stats = useMemo(() => {
    if (!records || records.length === 0) return null;
    const builder = new FlowGraphBuilder(records);
    return {
      totalFlows: builder.totalFlows,
      firstDistCount: builder.firstNodes.length,
      transitionCount: records.filter((r) => r.kind === 'TRANSITION').length,
    };
  }, [records]);

  const startingCount = useMemo(() => {
    if (!levels.length) return null;
    const firstLevel = levels[0];
    if (!firstLevel.nodes.length) return null;
    return firstLevel.nodes[0].count;
  }, [levels]);

  const startingKpiCount = useMemo(() => {
    if (!kpiEvent || !records) return null;
    if (!levels.length || !levels[0].nodes.length) return null;
    const builder = new FlowGraphBuilder(records);
    const firstNode = levels[0].nodes[0];
    const pathStr = `('${firstNode.event}',)`;
    const kpiPct = builder.kpiPercentages[pathStr];
    if (kpiPct == null) return null;
    return Math.round((firstNode.count * kpiPct) / 100);
  }, [kpiEvent, records, levels]);

  // Current path from levels
  const currentPath = useMemo(() => {
    return levels
      .filter((l) => l.selected && l.selected !== '(none)')
      .map((l) => l.selected!);
  }, [levels]);

  // Path flow count
  const pathFlows = useMemo(() => {
    for (let i = levels.length - 1; i >= 0; i--) {
      const lev = levels[i];
      if (lev.selected) {
        const node = lev.nodes.find((n) => n.event === lev.selected);
        if (node) return node.count;
      }
    }
    return 0;
  }, [levels]);

  /* ── Loading ───────────────────────────────────────────────────────────── */
  if (isLoading) {
    return (
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <Card className="flex flex-col items-center justify-center py-24 text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin mb-3" />
          <p className="text-sm">Loading flow data...</p>
        </Card>
      </motion.div>
    );
  }

  /* ── Error ──────────────────────────────────────────────────────────────── */
  if (isError) {
    return (
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <Card className="flex flex-col items-center justify-center py-16 text-red-500">
          <AlertCircle className="w-8 h-8 mb-3" />
          <p className="text-sm font-medium">Failed to load flow data</p>
          <p className="text-xs text-slate-500 mt-1">{error?.message}</p>
        </Card>
      </motion.div>
    );
  }

  /* ── Empty: not yet run ─────────────────────────────────────────────────── */
  if (!hasRun) {
    return (
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <Card className="flex flex-col items-center justify-center py-24">
          <GitBranch className="w-10 h-10 text-slate-300 mb-4" />
          <p className="text-sm font-medium text-slate-700 mb-1">
            Ready to analyze
          </p>
          <p className="text-xs text-slate-500 mb-4">
            Configure your options above and click Run
          </p>
          <Button size="sm" icon={<Play />} onClick={onRun} disabled={!canRun}>
            Run
          </Button>
        </Card>
      </motion.div>
    );
  }

  /* ── Empty: no data ─────────────────────────────────────────────────────── */
  if (!records || records.length === 0) {
    return (
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <Card className="flex flex-col items-center justify-center py-24">
          <GitBranch className="w-10 h-10 text-slate-300 mb-4" />
          <p className="text-sm font-medium text-slate-700">No data</p>
          <p className="text-xs text-slate-500 mt-1">
            No flow data found for the selected date range and filters
          </p>
        </Card>
      </motion.div>
    );
  }

  /* ── Results ────────────────────────────────────────────────────────────── */
  return (
    <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="space-y-3">
      {/* KPI Bar */}
      <div className="grid grid-cols-3 gap-3">
        <KpiCard label="Total Sessions" value={stats?.totalFlows ?? 0} color={accentColor} />
        <KpiCard label="Path Sessions" value={pathFlows} color={accentColor} />
        <KpiCard label="Depth Levels" value={levels.length} color={accentColor} />
      </div>

      {/* Breadcrumb */}
      {currentPath.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap text-sm">
          <span className="text-xs font-medium text-slate-500">Path:</span>
          {currentPath.map((event, idx) => (
            <span key={`${idx}-${event}`} className="flex items-center gap-1.5">
              {idx > 0 && <span className="text-slate-400">→</span>}
              <span
                className="px-2 py-0.5 rounded-md text-xs font-medium text-white"
                style={{ backgroundColor: accentColor }}
              >
                {event}
              </span>
            </span>
          ))}
          {levels.some((l) => l.selected === '(none)') && (
            <>
              <span className="text-slate-400">→</span>
              <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-slate-200 text-slate-600 italic">
                (none)
              </span>
            </>
          )}
        </div>
      )}

      {/* Flow Visualization */}
      <Card padding="none" className="overflow-hidden">
        <div style={{ height: 600 }}>
          <FlowVisualization
            records={records}
            levels={levels}
            onLevelsChange={onLevelsChange}
            kpiEvent={kpiEvent}
            startingCount={startingCount}
            startingKpiCount={startingKpiCount}
            accentColor={accentColor}
            onContinuationRequest={onContinuationRequest}
            continuationLoadingLevels={continuationLoadingLevels}
            continuationRecordsMap={continuationRecordsMap}
          />
        </div>
      </Card>
    </motion.div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function KpiCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Card className="px-4 py-3">
      <p className="text-xs text-slate-500 mb-0.5">{label}</p>
      <p className="text-lg font-semibold" style={{ color }}>
        {value.toLocaleString()}
      </p>
    </Card>
  );
}
